<template>
	<tbody>
		<tr class="list__row">
			<td colspan="1" class="list__row__item" @click="toggleRow">
				<SingleRankingDisplay
					v-if="title.streamingChartInfo?.rank"
					:rank="title.streamingChartInfo?.rank"
					:trend="title.streamingChartInfo?.trend"
					:trendDifference="title.streamingChartInfo?.trendDifference"
					:daysInTop1000="title.streamingChartInfo?.daysInTop1000"
					:withLogo="false"
					:withCondensed="true"
				/>
			</td>
			<td colspan="1" class="list__row__item poster">
				<div class="list__row__item__content" @click.self="toggleRow">
					<router-link :to="title.node.content.fullPath" @click.stop>
						<PictureComp
							:key="title.node.id"
							class="poster"
							:alt="title.node.content.title"
							:imageUrl="posterUrl"
							image-profile="poster"
						/>
					</router-link>
					<router-link :to="title.node.content.fullPath" @click.stop>
						<div class="list__row__item__content__title">
							<span>{{ getTitleName(title) }}</span>
							<span
								v-if="title.node.objectType === 'SHOW_SEASON'"
								class="list__row__item__content__title--season"
								>({{ getSeasonNumber(title) }})</span
							>
						</div>
					</router-link>
				</div>
			</td>
			<td colspan="1" class="list__row__item offers top-rank" @click="toggleRow">
				{{ padNumber(title?.streamingChartInfo?.topRank) }}
			</td>
			<td colspan="1" class="list__row__item offers">
				<OffersDisplay :title="title" />
			</td>
			<td colspan="1" class="list__row__item chevron" @click="toggleRow">
				<FontAwesomeIcon :icon="expandRow ? faChevronUp : faChevronDown" />
			</td>
		</tr>
		<tr class="list__row__expansion" colspan="5" :style="expandRow ? 'display: table-row' : 'display: none'">
			<td class="item-details" colspan="5">
				<div class="item-details__offers anti-offers">
					<h3 class="item-details__offers__title">{{ $t('WEBAPP_STREAMING') }}</h3>
					<OffersDisplay :title="title" :isMobile="true" />
				</div>
				<div class="item-details__historic">
					<div class="item-details__historic__item first-child">
						<h3 class="item-details__historic__item__title">{{ $t('WEBAPP_HIGHEST_RANK') }}</h3>
						<p class="item-details__historic__item__value">
							{{ padNumber(title.streamingChartInfo?.topRank) }}.
						</p>
					</div>
					<div class="item-details__historic__item">
						<h3 class="item-details__historic__item__title">{{ $t('WEBAPP_TOP3') }}</h3>
						<p class="item-details__historic__item__value">
							{{ numberOfDays(title.streamingChartInfo?.daysInTop3) }}
						</p>
					</div>
					<div class="item-details__historic__item">
						<h3 class="item-details__historic__item__title">{{ $t('WEBAPP_TOP10') }}</h3>
						<p class="item-details__historic__item__value">
							{{ numberOfDays(title.streamingChartInfo?.daysInTop10) }}
						</p>
					</div>
					<div class="item-details__historic__item">
						<h3 class="item-details__historic__item__title">{{ $t('WEBAPP_TOP100') }}</h3>
						<p class="item-details__historic__item__value">
							{{ numberOfDays(title.streamingChartInfo?.daysInTop100) }}
						</p>
					</div>
					<div class="item-details__historic__item">
						<h3 class="item-details__historic__item__title">{{ $t('WEBAPP_TOP1000') }}</h3>
						<p class="item-details__historic__item__value">
							{{ numberOfDays(title.streamingChartInfo?.daysInTop1000) }}
						</p>
					</div>
				</div>
				<div class="share-buttons">
					<p class="share-buttons__text">{{ $t('WEBAPP_SHARE_IT_ON') }}</p>
					<div class="share-buttons__container">
						<div
							class="share-buttons__button"
							@click="
								share(
									{
										titleId: title.node.objectId,
										titleObjectType: title.node.objectType,
										titleFullPath: title.node.content.fullPath,
										titleName: title.node.content.title,
									},
									ShareMethod.FACEBOOK
								)
							"
						>
							<FontAwesomeIcon :icon="faFacebook" />
						</div>
						<div
							class="share-buttons__button"
							@click="
								share(
									{
										titleId: title.node.objectId,
										titleObjectType: title.node.objectType,
										titleFullPath: title.node.content.fullPath,
										titleName: title.node.content.title,
									},
									ShareMethod.TWITTER
								)
							"
						>
							<FontAwesomeIcon :icon="faTwitter" />
						</div>
						<div
							class="share-buttons__button"
							@click="
								share(
									{
										titleId: title.node.objectId,
										titleObjectType: title.node.objectType,
										titleFullPath: title.node.content.fullPath,
										titleName: title.node.content.title,
									},
									ShareMethod.CLIPBOARD
								)
							"
						>
							<FontAwesomeIcon :icon="faCopy" />
						</div>
					</div>
				</div>
			</td>
		</tr>
	</tbody>
</template>

<script setup lang="ts">
import type { StreamingChartTitle } from './types';

import { computed, ref } from 'vue';
import { ObjectType } from '@/@types/graphql-types';
import PictureComp from '@/components/picture/PictureComp.vue';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import OffersDisplay from '@/components/streaming-charts/OffersDisplay.vue';

import { faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { ShareMethod } from '@/enums/share-methods';

import { copyToClipboard, getShareUrl } from '@/helpers/share-helper';
import { ClassicUrl, safeOpen } from '@/helpers/clickout-helper';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const props = defineProps<{
	title: StreamingChartTitle;
	index: number;
}>();

const expandRow = ref(false);
const toggleRow = () => {
	expandRow.value = !expandRow.value;
};

const { jwConfig } = useRuntimeConfig().public;
const posterUrl = computed(() => jwConfig.IMAGESCALER_URL + props.title.node.content.fullPosterUrl);

const getTitleName = (title: StreamingChartTitle) => {
	if (title.node.objectType === ObjectType.ShowSeason) {
		// @ts-ignore
		return title?.node?.show?.content?.title;
	}
	return title?.node?.content?.title;
};

const { t } = useI18n();
function getSeasonNumber(title: StreamingChartTitle) {
	// @ts-ignore
	return `${t('WEBAPP_SEASON')} ${title?.node?.content?.seasonNumber}`;
}

function numberOfDays(days: number | undefined) {
	if (!days) return '--';
	return days === 1 ? `${days} ${t('WEBAPP_DAY')}` : t('WEBAPP_X_DAYS', { number: days });
}

function padNumber(number: number | undefined) {
	return number?.toString().padStart(2, '0') ?? number;
}

function share(
	titleDetails: { titleId: number; titleObjectType: ObjectType; titleFullPath: string; titleName: string },
	method?: ShareMethod
) {
	const shareUrl = getShareUrl(titleDetails.titleFullPath);
	const shareText = `${titleDetails.titleName}:`;

	switch (method) {
		case ShareMethod.FACEBOOK:
			safeOpen(
				new ClassicUrl('http://www.facebook.com/sharer/sharer.php').set('u', shareUrl).set('t', shareText)
			);
			break;
		case ShareMethod.TWITTER:
			safeOpen(new ClassicUrl('https://twitter.com/intent/tweet').set('url', shareUrl).set('text', shareText));
			break;
		default:
			copyToClipboard(shareUrl);
	}
}
</script>

<style lang="scss" scoped>
.list {
	width: 100%;
	border-spacing: 0;

	&__row {
		&:hover {
			cursor: pointer;
		}
		&__item {
			border-bottom: 1px solid #1c252f;
			padding: 8px;
			display: table-cell;

			@media (max-width: 700px) {
				padding-inline-start: 0;
			}

			&__content {
				display: flex;
				align-items: center;

				.poster {
					display: block;
					z-index: 10;
					width: 48px;
					min-width: 48px;
					height: 68px;
					border-radius: 2px;

					margin-right: 16px;

					&:hover {
						cursor: pointer;
					}

					:deep(img) {
						border-radius: 2px;
					}
				}

				&__title {
					color: #c6c8cd;
					font-size: 16px;
					font-weight: 900;
					display: flex;
					flex-direction: column;
					line-height: 24px;
					letter-spacing: 0.24px;

					&--season {
						font-weight: 400;
					}
				}
			}
		}

		&__expansion {
			height: fit-content;
			table-layout: fixed;
			border-collapse: collapse;
			width: 100%;

			&--hide {
				display: none !important;
			}

			.item-details {
				width: 100%;
				height: fit-content;
				padding: 16px 0;

				&__offers {
					width: 100%;
					background-color: #0f0f0f;
					padding: 16px;
					margin-bottom: 16px;
					border-radius: 8px;

					&__title {
						color: #6f7d90;
						font-size: 16px;
						font-weight: 400;
						letter-spacing: 0.24px;
						margin-top: 0;
					}
				}

				&__historic {
					display: grid;
					grid-template-columns: 1fr 1fr 1fr;
					grid-template-rows: 1fr 1fr;
					grid-gap: 16px;

					@media (max-width: 700px) {
						grid-template-columns: 1fr 1fr;
						grid-template-rows: 1fr 1fr 1fr;
					}

					&__item {
						background-color: #0f0f0f;
						padding: 16px;
						border-radius: 8px;

						&__title {
							color: #6f7d90;
							font-size: 16px;
							font-weight: 400;
							letter-spacing: 0.24px;
							margin-top: 0;
							margin-bottom: 32px;

							@media (max-width: 700px) {
								margin-bottom: 16px;
							}
						}

						&__value {
							color: #c6c8cd;
							font-size: 22px;
							font-weight: 900;
							line-height: 32px;
							letter-spacing: 0.22px;
							margin: 0;
						}
					}

					.first-child {
						grid-row: 1 / 3;

						@media (max-width: 700px) {
							grid-row: initial;
							grid-column: 1 / 3;
						}
					}
				}
			}
		}
	}
}

.poster {
	padding-inline-start: 0;
}

.offers {
	@media (max-width: 700px) {
		display: none;
	}
}

.anti-offers {
	@media (min-width: 700px) {
		display: none;
	}
}

.top-rank {
	text-align: center;
}

.chevron {
	text-align: center;
	&:hover {
		cursor: pointer;
	}
}

.share-buttons {
	display: flex;
	align-items: center;
	padding-top: 0;
	color: #6f7d90;

	&__text {
		color: #6f7d90;
		font-size: 16px;
	}

	&__container {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		font-size: 16px;
		margin-inline-start: 8px;
	}

	&__button {
		cursor: pointer;

		&:not(:first-of-type) {
			margin-inline-start: 8px;
		}
	}
}
</style>
